import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2023 semantics GmbH
// Date        : $Date: 2026-09-04 10:42:14 +0200 (Fri, 04 Sep 2026) $
// Author      : Dirk Rothe
// Worker      : $Author: o.powroznik $
// Revision    : $Rev: 165253 $
// Purpose     : hooks for presentation module
// =============================================================================
// ---Third-party imports
import React from 'react';
import { values, reversed, merge } from 'ldash';
// ---Framework imports
import log from 'uix/logging';
import { mapItems, isString, range, dict, flatten, callIfFunc, keys } from 'ldash';
import { useMemo, useRef, useEffect, useLayoutEffect, useDimensions, useResizeObserver, useCallback, useDebounce, useState, useSelector, useResettableState, useDelay, useRouter, useApi, usePrevious, useCallApi, useServerEvents } from 'uix/hooks';
import { Sel } from 'uix/registries';
import { useDomainTypes } from 'uix/types/hooks';
import { resolveWidgets } from 'uix/wiki/resolve';
import { RawLink } from 'uix/components/links';
import { VIcon } from 'uix/graphics';
import { useLanguageSwitching } from 'uix/i18n/hooks';
// ---Local imports
import S from './index.css';
import { WIDGET_TYPES, SLIDE_TYPES } from './constants';
import { validateMeta, getContrastColors } from './utils';
// ---Exports
export const usePresentationData = ({ node, filterId, pendingChanges })=>{
    let api_name = 'presentation/getPresentationTree';
    let slideSchema = useSlideSchemaFlat();
    let language = useSelector(Sel.language);
    let preview = useSelector((root)=>root.data.wiki.preview);
    // preview[otid]?.rendered
    let [reloadedNodes, setReloadedNodes] = useState({});
    let { getType } = useDomainTypes();
    let root_node = node.ancestorsAndSelf.find((n)=>n.type == 'exhibition');
    if (!root_node && node.type == 'exhibitions') {
        root_node = node;
    }
    let { data, loading, reload: reloadFullStructure } = useApi(api_name, {
        otid: root_node?.otid,
        language
    }, {
        autoreset: false,
        condition: !!root_node
    });
    let res = useMemo(()=>{
        let rows = data?.presentationData || [];
        // TODO: TEMPORARY — backend api.py returns duplicates from resolvePresentationContext merge
        // Fix: deduplicate at source in api.py lines 234-235, only extend with unique otids
        let seenOtids = new Set();
        rows = rows.filter((r)=>{
            if (seenOtids.has(r.otid)) return false;
            seenOtids.add(r.otid);
            return true;
        });
        let nodes = rows.map((r)=>reloadedNodes[r.otid] || r);
        let parentsById = dict(nodes.map((n)=>[
                n.otid,
                n.parent
            ]));
        let getAncestors = (n)=>{
            let a = [];
            let p = n.parent;
            while(p && parentsById[p]){
                a.push(p);
                p = parentsById[p];
            }
            return a;
        };
        nodes = nodes.map((n)=>{
            let node = {
                ...n,
                ancestors: getAncestors(n),
                type: getType(n.datatype, n.subtype)
            };
            let preview_rendered = preview[n.otid]?.rendered;
            if (preview_rendered && n.content) {
                node = {
                    ...node,
                    content: {
                        ...node.content,
                        rendered: preview_rendered
                    }
                };
            }
            return node;
        });
        let nodesById = dict(nodes.map((n)=>[
                n.otid,
                n
            ]));
        let deriveFromAncestors = (n)=>{
            // only derive meta
            if (!SLIDE_TYPES.includes(n.type.name)) return n;
            let ancestorsAndSelf = reversed([
                n,
                ...n.ancestors.map((otid)=>nodesById[otid]).filter(Boolean)
            ]);
            let meta = merge({}, ...ancestorsAndSelf.map((a)=>({
                    ...a.original?.meta || a.meta || {},
                    ...pendingChanges?.[a.otid] || {}
                })));
            return {
                ...n,
                meta: validateMeta(n.type.name, meta, slideSchema, n.otid),
                original: n.original || {
                    meta: n.meta
                }
            };
        // additional backgound merging (dirk: I think it's only disabled due to room-slide introduction)
        //~ let background = merge({}, ...ancestorsAndSelf.map(n => n.background).filter(Boolean))
        //~ return {...n, meta, background, original: {meta: n.meta, background: n.background}}
        };
        nodes = nodes.map(deriveFromAncestors);
        // Inject pendingChanges before resolveWidgets to see changes (PropertyEditor)
        if (pendingChanges && keys(pendingChanges).length) {
            nodes = nodes.map((n)=>{
                if (SLIDE_TYPES.includes(n.type.name)) return n;
                let changes = pendingChanges[n.otid];
                if (!changes) return n;
                // preserve original meta to track modification
                return {
                    ...n,
                    original: {
                        meta: n.meta
                    },
                    meta: {
                        ...n.meta || {},
                        ...changes
                    }
                };
            });
        }
        nodes = resolveWidgets({
            nodes
        });
        // rebuild with enriched nodes
        nodesById = dict(nodes.map((n)=>[
                n.otid,
                n
            ]));
        let rootId = nodes.filter((e)=>e.type.name === 'exhibition')[0]?.otid;
        return {
            nodes,
            nodesById,
            loading,
            rootId,
            rootNode: {
                ...nodesById[rootId],
                ...root_node
            }
        };
    }, [
        data,
        preview,
        loading,
        reloadedNodes,
        pendingChanges
    ]);
    res = useMemo(()=>{
        if (!filterId) return res;
        let node = res.nodesById[filterId];
        if (!node) return res;
        let nodes = res.nodes.filter((n)=>n.otid == filterId || n.ancestors.includes(filterId) || node.ancestors.includes(n.otid));
        return {
            ...res,
            nodes
        };
    }, [
        res,
        filterId
    ]);
    let { callApi: callReloadApi } = useCallApi(api_name);
    let [reloaded, setReloaded] = useState({}) // {otid: reload_ts}
    ;
    // note: lets a caller push a just-saved meta in immediately, skipping the EWikidataChanged round trip
    let mergeNodeMeta = useCallback((mergeOtid, meta)=>{
        setReloadedNodes((prev)=>{
            let base = prev[mergeOtid] || res.nodesById[mergeOtid];
            if (!base) return prev;
            // note: drop the stale derived .original, else deriveFromAncestors reuses its old meta
            let { original, ...rawBase } = base;
            return {
                ...prev,
                [mergeOtid]: {
                    ...rawBase,
                    meta
                }
            };
        });
        // note: marks it reloaded, skips the redundant own-save EWikidataChanged round trip
        setReloaded((prev)=>({
                ...prev,
                [mergeOtid]: Date.now()
            }));
    }, [
        res
    ]);
    // reload nodes on editing ops
    let evts = useServerEvents([
        'ECaptionChanged',
        'EWikidataChanged',
        'ERecordDataChanged',
        'EPageboxUpdate',
        'ERecordLinked',
        // structural changes
        'ERecordInserted',
        'ERecordMoved',
        'ERecordDeleted'
    ]);
    let norm_otid = (evt)=>(otid)=>({
                otid,
                ts: evt.evt_ts
            });
    useEffect(()=>{
        let evts_filtered = [
            ...flatten(evts.ERecordDataChanged.map((evt)=>evt.otids.map(norm_otid(evt)))),
            ...[
                ...evts.EWikidataChanged,
                ...evts.ECaptionChanged
            ].map((evt)=>norm_otid(evt)(evt.otid)),
            ...evts.EPageboxUpdate.map((evt)=>norm_otid(evt)(evt.relotid)),
            // pick parent id for update (target)
            ...evts.ERecordLinked.filter((e)=>[
                    'last',
                    'first'
                ].includes(e.position)).map((evt)=>norm_otid(evt)(evt.targetid)),
            // parent of target
            ...evts.ERecordLinked.filter((e)=>[
                    'before',
                    'after'
                ].includes(e.position)).map((evt)=>norm_otid(evt)(evt.targetparent))
        ];
        let need_reload = evts_filtered.filter(({ otid, ts })=>res.nodesById[otid] && (!reloaded[otid] || reloaded[otid] < ts));
        if (need_reload.length > 0) {
            let nextReloaded = dict(need_reload.map(({ otid, ts })=>[
                    otid,
                    ts
                ]));
            callReloadApi({
                otids: keys(nextReloaded).join(','),
                language
            }, (data, error)=>{
                if (error) {
                    log.error(error);
                    return;
                }
                log.debug('reloaded %s presentation nodes', data.presentationData.length);
                let rn = dict(data.presentationData.map((n)=>[
                        n.otid,
                        n
                    ]));
                setReloadedNodes((prev)=>({
                        ...prev,
                        ...rn
                    }));
            });
        }
    }, [
        evts.EWikidataChanged,
        evts.ERecordDataChanged,
        evts.ECaptionChanged,
        evts.EPageboxUpdate,
        evts.ERecordLinked
    ]);
    useEffect(()=>{
        let struct_reload_ts = reloaded[null] ?? null;
        let trigger_otids = new Set(values(res.nodesById).map((n)=>String(n.otid)));
        // XXX: what about otparent_old in ERecordMoved
        let evts1 = [
            ...evts.ERecordInserted,
            ...evts.ERecordMoved
        ].map((evt)=>norm_otid(evt)(evt.otparent));
        let evts2 = flatten(evts.ERecordDeleted.map((evt)=>evt.parent_otids.map(norm_otid(evt))));
        let evts_filtered = [
            ...evts1,
            ...evts2
        ];
        // check if the otid is in structure
        let need_reload = evts_filtered.filter((d)=>trigger_otids.has(String(d.otid)) && (struct_reload_ts === null || d.ts > struct_reload_ts));
        if (need_reload.length > 0) {
            log.debug('reloaded presentation structure');
            setReloaded({
                ...reloaded,
                null: Math.max(...need_reload.map((d)=>d.ts))
            });
            reloadFullStructure();
        }
    }, [
        evts.ERecordInserted,
        evts.ERecordMoved,
        evts.ERecordDeleted
    ]);
    return {
        ...res,
        mergeNodeMeta
    };
};
// return new values for transition progress only each 100ms
let PROGRESS_DEBOUNCE_MS = 200;
if (process.env.NODE_ENV === 'production') {
    // more fps
    PROGRESS_DEBOUNCE_MS = 100;
}
const zeroPosition = {
    x: 0,
    y: 0
};
let intify = ({ x, y })=>({
        x: Math.floor(x),
        y: Math.floor(y)
    });
const getScrollPosition = ({ element, boundingElement })=>{
    /* get top left position of element with respect to boundingElement
   */ let targetPosition = element?.current?.getBoundingClientRect();
    let containerPosition = boundingElement?.current?.getBoundingClientRect();
    return containerPosition ? {
        x: (containerPosition.x || 0) - (targetPosition.x || 0),
        y: (containerPosition.y || 0) - (targetPosition.y || 0)
    } : targetPosition ? {
        x: targetPosition.left,
        y: targetPosition.top
    } : zeroPosition;
};
const useScrollPosition = (element, boundingElement)=>{
    let [pos, setPos] = useState(zeroPosition);
    // return new values only each 5ms (200fps)
    let [position] = useDebounce(pos, 5);
    useLayoutEffect(()=>{
        if (boundingElement.current && element.current) {
            let handleScroll = ()=>setPos(intify(getScrollPosition({
                    element,
                    boundingElement
                })));
            boundingElement.current?.addEventListener('scroll', handleScroll, {
                passive: true
            });
            // initialize once
            handleScroll();
            return ()=>{
                boundingElement.current?.removeEventListener('scroll', handleScroll);
            };
        }
    }, [
        boundingElement.current,
        element.current
    ]);
    return position;
};
const useScrollProgress = (element)=>{
    let [pos, setPos] = useState({
        percent: 0,
        absolute: 0
    });
    let [position] = useDebounce(pos, PROGRESS_DEBOUNCE_MS);
    useLayoutEffect(()=>{
        if (element.current) {
            let handleScroll = ()=>{
                let e = element?.current;
                let progress = e?.scrollTop || 0;
                let total = e?.scrollHeight - e?.offsetHeight || 1;
                setPos({
                    percent: Math.floor(Math.min(100, progress / total * 100)),
                    absolute: Math.floor(progress)
                });
            };
            element.current?.addEventListener('scroll', handleScroll, {
                passive: true
            });
            // initialize once
            handleScroll();
            return ()=>{
                element.current?.removeEventListener('scroll', handleScroll);
            };
        }
    }, [
        element.current
    ]);
    return position;
};
const TransitionDetector = ({ boundingElement, debug, index, boundingHeight, onTransition, onScrolledChange, scrolled })=>{
    /* build div (before slide) with height=0 for tracking scroll positions
   */ let element = useRef();
    let scrollPosition = useScrollPosition(element, boundingElement);
    let [sendValues, setSendValues] = useState({
        progressPercent: 0,
        isScrolled: false
    });
    useEffect(()=>{
        setSendValues((sv)=>sv.isScrolled === scrolled ? sv : {
                ...sv,
                isScrolled: scrolled
            });
    }, [
        scrolled
    ]);
    let _onTransition = (progressPercent)=>{
        onTransition({
            progressPercent
        });
        setSendValues((sendValues)=>({
                ...sendValues,
                progressPercent
            }));
    };
    useEffect(()=>{
        if (scrollPosition.x == 0 && scrollPosition.y == 0) return; // do not send zeroPosition values
        // use 99 instead of 100, setActiveSlide scrolling should not activate the previous slide
        const endPercent = 99;
        //~ log.x('%o', boundingHeight + scrollPosition.y)
        let progressPercent = (boundingHeight + scrollPosition.y) / boundingHeight * 100;
        // only inform consumer if between 0 and 100
        if (progressPercent >= 0 && progressPercent <= endPercent) {
            _onTransition(progressPercent);
        } else {
            // send final 0% and 100% values (if <0 and >endPercent)
            if (sendValues.progressPercent > 0 && sendValues.progressPercent < endPercent) {
                _onTransition(progressPercent < 0 ? 0 : 100);
            }
        }
        let isScrolled = progressPercent >= endPercent;
        if (isScrolled != sendValues.isScrolled) {
            setSendValues((sendValues)=>({
                    ...sendValues,
                    isScrolled
                }));
            onScrolledChange(isScrolled);
        }
    // if slide changes height, boundingHeight will change
    }, [
        scrollPosition,
        boundingHeight
    ]);
    let debugS = {
        outline: '0.5px solid blue',
        zIndex: 200,
        position: 'relative',
        height: '0px'
    };
    return /*#__PURE__*/ _jsx("div", {
        ref: element,
        style: debug ? debugS : null,
        children: debug && /*#__PURE__*/ _jsxs("span", {
            style: {
                color: 'blue',
                backgroundColor: 'white',
                float: 'right',
                fontSize: '12px'
            },
            children: [
                `[${index}] `,
                sendValues.progressPercent.toFixed(2),
                "%"
            ]
        })
    });
};
const SlideContainer = ({ onRef, children, ...props })=>{
    /* capture dom node for slide
   */ let ref = useRef();
    useEffect(()=>{
        onRef(ref);
    }, []);
    return /*#__PURE__*/ _jsx("div", {
        ...props,
        ref: ref,
        children: children
    });
};
const useIdSync = ({ enabled, rootNode, slides, activeSlide, setActiveSlide, slideRefs })=>{
    /* syncronize request.id <-> activeSlide
   */ let { request, replaceQueryParams } = useRouter();
    // direction request.id -> activeSlide is only active if init is true
    let [init, setInit] = useResettableState(true, [
        rootNode?.otid,
        request.id
    ]);
    // XXX: the direction: request.id -> activeSlide is not really stable
    // disable this direction after 1000ms
    //~ useDelay(() => setInit(false), 1000, [rootNode?.otid])
    let [debouncedActiveSlide] = useDebounce(activeSlide, 500, {
        leading: false
    });
    useEffect(()=>{
        // direction: activeSlide -> request.id
        if (!init && enabled && slides?.[debouncedActiveSlide]) {
            let id = slides[debouncedActiveSlide].otid;
            // prevent changing query param during init on subslides
            let req_is_subslide = slides[debouncedActiveSlide].subslides.findIndex((sl)=>sl.otid == request.id) > -1;
            if (!req_is_subslide) {
                replaceQueryParams({
                    id
                });
            }
        }
    }, [
        enabled,
        debouncedActiveSlide
    ]);
    useEffect(()=>{
        // direction: request.id -> activeSlide
        let isInSubSlides = (s)=>s.subslides.findIndex((sl)=>request.id == sl.otid) > -1;
        let isRoomSlide = (s)=>s.roomStart && request.id == s.parent && !s.isRoomSlide;
        if (init && enabled && slides && setActiveSlide) {
            let i = slides.findIndex((s)=>request.id == s.otid || isRoomSlide(s) || isInSubSlides(s));
            if (i == -1) {
                // goto first if id is presentation root id
                //~ if (slides.find(s => s.ancestors.includes(Number(request.id)))) {
                if (rootNode?.otid == request.id) {
                    i = 0;
                } // else do nothing
            }
            // wait for available slideRef dom node
            // XXX: we have once checked for "i != activeSlide" but for slideFocus mode we want to ensure
            // that we go to start of current slide independently of slide index change
            if (slideRefs[i]?.current && i > -1) {
                log.info('initial active slide index to %o', i);
                setActiveSlide(i, 'instant');
                setInit(false);
            }
        }
    }, [
        enabled,
        slides,
        request.id,
        activeSlide,
        setActiveSlide,
        init,
        slideRefs
    ]);
};
export const useSubSlideIdSync = ({ slide, subslides, onSubSlideChange, active })=>{
    let { request, replaceQueryParams } = useRouter();
    let prevActive = usePrevious(active, false) // init with false
    ;
    // switch only if true -> false transition (not on initial false -> true transition)
    let deactivating = prevActive && !active;
    // idx = 0: no subslide selected
    // idx = 1: first subslide active
    let [activeSubSlideIdx, _setActiveSubSlideIdx] = useResettableState(0, [
        request.id,
        deactivating
    ]);
    let setActiveSubSlideIdx = useCallback((subSlideIdx)=>{
        // direction: subslide => request.id
        _setActiveSubSlideIdx(subSlideIdx);
        let sl = slide // subSlideIdx == 0 is main slide (otid)
        ;
        if (subSlideIdx > 0) {
            sl = subslides[subSlideIdx - 1];
        }
        if (sl) {
            replaceQueryParams({
                id: sl.otid
            });
            callIfFunc(onSubSlideChange, {
                slide: sl
            });
        }
    }, [
        _setActiveSubSlideIdx,
        replaceQueryParams
    ]);
    useEffect(()=>{
        // direction: request.id => subslide
        let idx = subslides.findIndex((sl)=>sl.otid == request.id);
        if (idx > -1) {
            _setActiveSubSlideIdx(idx + 1);
            callIfFunc(onSubSlideChange, {
                slide: subslides[idx]
            });
        } else if (request.id == slide.otid) {
            // main slide
            callIfFunc(onSubSlideChange, {
                slide
            });
        }
    }, [
        request.id,
        subslides
    ]);
    return {
        activeSubSlideIdx,
        setActiveSubSlideIdx
    };
};
// * scrolls a widget into view once per refId, or opens Modals* //
export const useRefIdScroll = (refId, scopeId)=>{
    let done = useRef(null);
    useEffect(()=>{
        if (!refId || !scopeId || done.current === refId) return;
        let scope = document.getElementById(scopeId);
        let esc = CSS.escape(String(refId));
        let el = scope?.querySelector(`[refid="${esc}"], [data-modal-refid="${esc}"]`);
        if (el) {
            el.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            done.current = refId;
        }
    }, [
        refId,
        scopeId
    ]);
};
// * bidirectional sync between a modal's open state and `?wid=<refid>`, analog to useSubSlideIdSync/id
export const useModalWidSync = (otid, modalOpen, setModalOpen)=>{
    let { request, replaceQueryParams } = useRouter();
    let wid = request.query.wid;
    useEffect(()=>{
        // direction: request.wid -> modalOpen
        if (!modalOpen && wid && wid === otid) setModalOpen(true);
    }, [
        wid,
        otid
    ]);
    let openModal = useCallback(()=>{
        // direction: modalOpen -> request.wid
        setModalOpen(true);
        replaceQueryParams({
            wid: otid
        });
    }, [
        otid,
        setModalOpen,
        replaceQueryParams
    ]);
    let closeModal = useCallback(()=>{
        setModalOpen(false);
        replaceQueryParams({
            wid: null
        });
    }, [
        setModalOpen,
        replaceQueryParams
    ]);
    return {
        openModal,
        closeModal
    };
};
// positioned before (minus distance) slide content for scroll-to action
const ScrollTarget = ({ distance })=>/*#__PURE__*/ _jsx("div", {
        style: {
            height: 0,
            marginTop: `-${distance}`
        }
    });
export const useSlides = ({ rootNode, slides, debug = false, transitionCooldown = 300, syncId = false })=>{
    // track dimensions of boundingElement
    let [dimensions, boundingElement] = useDimensions({
        delta: 5
    });
    let scrollProgress = useScrollProgress(boundingElement);
    let progressPercent = scrollProgress?.percent || 0;
    let progressAbsolute = scrollProgress?.absolute || 0;
    // track dimensions of slides (XXX: use to update transitions?)
    //~ let [slideDims, setSlideDims] = useState({})
    // track slide dom nodes, reset if slideset changes
    let resetDep = [
        rootNode?.otid
    ];
    //~ log.x(rootNode)
    let [slideRefs, setSlideRefs] = useResettableState({}, resetDep);
    // scroll to slide
    let setActiveSlide = (index, scrollBehavior)=>{
        let ref = slideRefs[index];
        if (ref?.current) {
            setIsScrolled(dict(range(index + 1).map((i)=>[
                    i,
                    true
                ])));
            // scroll to slide
            let behavior = scrollBehavior || (Math.abs(activeSlide - index) == 1 ? 'smooth' : 'instant');
            // * Cancel any running transition on jumps
            if (behavior === 'instant') setTrans({
                progress: null,
                slide: null,
                start: false
            });
            // selecting ScrollTarget here (slide 0 uses slideStart instead of contentStart)
            let slideStart = ref.current;
            let contentStart = ref.current.firstChild;
            let target = index > 0 ? contentStart || slideStart : slideStart;
            target.scrollIntoView({
                behavior,
                block: 'start',
                inline: 'nearest'
            });
        } else {
            log.warn('no slide ref for %s', index);
        }
    };
    // track active transition
    let [trans, setTrans] = useState({
        progress: null,
        slide: null,
        start: false
    });
    // track if slide idxs are "in the past" with respect to forward scrolling
    let [isScrolled, setIsScrolled] = useResettableState({}, resetDep) // {slideIdx: bool}
    ;
    let changeTrans = (i, progressPercent)=>{
        // purpose: prevents visual jumps from 0/null progess noise
        setTrans((currentTrans)=>{
            // if slide i is already tracked, only update on real changes
            if (currentTrans.slide === i) {
                if (currentTrans.progress === progressPercent) return currentTrans;
                return {
                    ...currentTrans,
                    progress: progressPercent
                };
            }
            // only accept transition if it's active (>0)
            if (progressPercent > 0) {
                return {
                    slide: i,
                    progress: progressPercent,
                    start: currentTrans.progress === null
                };
            }
            return currentTrans;
        });
    };
    let activeSlide = useMemo(()=>Math.max(0, ...mapItems(isScrolled, (idx)=>idx, (idx, value)=>value)), [
        isScrolled
    ]);
    useIdSync({
        enabled: syncId,
        rootNode,
        slides,
        activeSlide,
        setActiveSlide,
        slideRefs
    });
    let transitionDone = ()=>trans.progress == 100 || trans.progress == 0;
    useDelay(()=>transitionDone() ? setTrans({
            progress: null,
            slide: null,
            start: false
        }) : null, transitionCooldown, [
        transitionDone()
    ]);
    // * transition is only real when crossing into the next slide
    let transitionProgress = trans.slide === activeSlide + 1 ? trans.progress : null;
    let buildSlideContainer = ({ key, index, content, ...props })=>{
        return /*#__PURE__*/ _jsxs(React.Fragment, {
            children: [
                index > 0 && /*#__PURE__*/ _jsx(TransitionDetector, {
                    index: index,
                    debug: debug,
                    boundingElement: boundingElement,
                    boundingHeight: dimensions.height,
                    scrolled: !!isScrolled[index],
                    onTransition: ({ progressPercent })=>changeTrans(index, progressPercent),
                    onScrolledChange: (value)=>setIsScrolled((prev)=>prev[index] === value ? prev : {
                                ...prev,
                                [index]: value
                            })
                }),
                /*#__PURE__*/ _jsxs(SlideContainer, {
                    ...props,
                    onRef: (ref)=>{
                        setSlideRefs((slideRefs)=>({
                                ...slideRefs,
                                [index]: ref
                            }));
                    },
                    children: [
                        index > 0 && /*#__PURE__*/ _jsx(ScrollTarget, {
                            distance: "2em"
                        }),
                        content
                    ]
                })
            ]
        }, key);
    };
    return {
        transitionProgress,
        transitionStart: trans.start,
        progressPercent,
        progressAbsolute,
        activeSlide,
        setActiveSlide,
        dimensions,
        boundingElement,
        buildSlideContainer,
        debugData: {
            progressPercent,
            activeSlide,
            trans,
            transitionProgress,
            dimensions,
            slides
        }
    };
};
const stringOrDangerHtml = (s)=>{
    if (isString(s) && s.includes('<') && s.includes('>')) {
        return s = /*#__PURE__*/ _jsx("span", {
            dangerouslySetInnerHTML: {
                __html: s
            }
        });
    } else return s;
};
const getBrowserLanguage = (availableLangs)=>{
    if (!availableLangs?.length) return null;
    let browserLangs = (navigator.languages || [
        navigator.language
    ]).map((l)=>l.split('-')[0].toLowerCase());
    return browserLangs.map((bl)=>availableLangs.find((lang)=>lang.toLowerCase() === bl)).find(Boolean) || null;
};
export const usePresLanguage = (enableLang)=>{
    let { selected, supported, auxiliary, switchLanguage } = useLanguageSwitching();
    let supportedLangs = [
        ...supported || [],
        ...auxiliary || []
    ];
    enableLang = enableLang?.split(',').map((s)=>s.trim()).filter(Boolean) || [];
    let validLangs = supportedLangs.filter((lang)=>enableLang.includes(lang));
    validLangs.length < enableLang.length && log.warn(`Unsupported Presentation Language: ${enableLang.filter((lang)=>!validLangs.includes(lang)).join(', ')}`);
    let presLang = enableLang.includes('none') || !enableLang.length ? supportedLangs : validLangs;
    // if current language not in presLang, switch to browser language if allowed, else first
    useEffect(()=>{
        if (presLang?.length > 0 && !presLang.includes(selected)) {
            switchLanguage(getBrowserLanguage(presLang) || presLang[0]);
        }
    }, [
        presLang,
        selected,
        switchLanguage
    ]);
    return {
        presLang,
        selected,
        switchLanguage
    };
};
export const useCaptionLink = (caption, linkid, url, id, icon, hideLink)=>{
    let apiurl = useSelector(Sel.apiurl);
    let link = linkid || id;
    if (caption) {
        if (url) {
            return /*#__PURE__*/ _jsxs("p", {
                className: S.caption,
                children: [
                    stringOrDangerHtml(caption),
                    ' ',
                    icon && !hideLink && /*#__PURE__*/ _jsx(VIcon, {
                        name: "link",
                        href: url,
                        target: "_blank"
                    })
                ]
            });
        }
        if (link) {
            return /*#__PURE__*/ _jsxs("p", {
                className: S.caption,
                children: [
                    stringOrDangerHtml(caption),
                    icon && ' ',
                    icon && !hideLink && /*#__PURE__*/ _jsx(VIcon, {
                        name: "link",
                        href: apiurl.baseUrl('/' + link),
                        target: "_blank"
                    })
                ]
            });
        } else {
            return /*#__PURE__*/ _jsx("p", {
                className: S.caption,
                children: stringOrDangerHtml(caption)
            });
        }
    } else {
        return null;
    }
};
export const usePageIdMdCaption = (pageid)=>{
    let { data, loading } = useApi('filter/ancestorAxisJson', {
        id: pageid
    }, {
        condition: !!pageid
    });
    return data?.ancestorAxis.filter((e)=>e.ot_rcv_datatype == 1).at(0)?.ot_caption;
};
// swipe gesture detection
export const useSwipeGesture = (callbacks = {}, options = {})=>{
    const { onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown } = callbacks;
    const { threshold = 50 /* minimum distance in px for a swipe */ , disabled = false } = options;
    // enable vertical swipes on
    const enableVertical = !!(onSwipeUp && onSwipeDown);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [startY, setStartY] = useState(0);
    const [dragOffset, setDragOffset] = useState(0);
    const [dragOffsetY, setDragOffsetY] = useState(0);
    const handlePointerDown = useCallback((e)=>{
        if (disabled) return;
        setIsDragging(true);
        setStartX(e.clientX || e.touches?.[0]?.clientX || 0);
        setStartY(e.clientY || e.touches?.[0]?.clientY || 0);
        setDragOffset(0);
        setDragOffsetY(0);
    }, [
        disabled
    ]);
    const handlePointerMove = useCallback((e)=>{
        if (!isDragging || disabled) return;
        const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
        const currentY = e.clientY || e.touches?.[0]?.clientY || 0;
        const offsetX = currentX - startX;
        const offsetY = currentY - startY;
        setDragOffset(offsetX);
        setDragOffsetY(offsetY);
        // Prevent Y-scrolling
        if (enableVertical) {
            if (Math.abs(offsetX) > Math.abs(offsetY)) {
                e.preventDefault();
            }
        } else {
            e.preventDefault();
        }
    }, [
        isDragging,
        disabled,
        startX,
        startY,
        enableVertical
    ]);
    const handlePointerUp = useCallback(()=>{
        if (!isDragging || disabled) return;
        setIsDragging(false);
        const horizontalDistance = Math.abs(dragOffset);
        const verticalDistance = Math.abs(dragOffsetY);
        if (horizontalDistance > verticalDistance) {
            // Horizontal swipe
            if (horizontalDistance > threshold) {
                if (dragOffset > 0 && onSwipeRight) {
                    onSwipeRight();
                } else if (dragOffset < 0 && onSwipeLeft) {
                    onSwipeLeft();
                }
            }
        } else if (enableVertical && verticalDistance > threshold) {
            // Vertical swipe
            if (dragOffsetY > 0 && onSwipeDown) {
                onSwipeDown();
            } else if (dragOffsetY < 0 && onSwipeUp) {
                onSwipeUp();
            }
        }
        setDragOffset(0);
        setDragOffsetY(0);
    }, [
        isDragging,
        disabled,
        dragOffset,
        dragOffsetY,
        threshold,
        onSwipeLeft,
        onSwipeRight,
        onSwipeUp,
        onSwipeDown,
        enableVertical
    ]);
    const swipeHandlers = {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onTouchStart: handlePointerDown,
        onTouchMove: handlePointerMove,
        onTouchEnd: handlePointerUp
    };
    return {
        isDragging,
        dragOffset,
        dragOffsetY,
        swipeHandlers
    };
};
export const useWidgetSchema = ()=>{
    let widgetSchema = useSelector((root)=>root.config.widgetSchema);
    return useMemo(()=>{
        let flat_sections = (v)=>flatten(mapItems(v, (section, values)=>values));
        let schema = dict(mapItems(widgetSchema, (k, v)=>[
                k,
                flat_sections(v)
            ], (k)=>WIDGET_TYPES.includes(k)));
        return schema;
    }, [
        widgetSchema
    ]);
};
export const useSlideSchema = ()=>{
    let widgetSchema = useSelector((root)=>root.config.widgetSchema);
    return useMemo(()=>{
        return dict(mapItems(widgetSchema, (k, v)=>[
                k.toLowerCase(),
                v
            ], (k)=>SLIDE_TYPES.includes(k.toLowerCase())));
    }, [
        widgetSchema
    ]);
};
export const useSlideSchemaFlat = ()=>{
    let schema = useSlideSchema();
    return useMemo(()=>{
        let flat_sections = (v)=>flatten(mapItems(v, (section, values)=>values));
        return dict(mapItems(schema, (k, v)=>[
                k,
                flat_sections(v)
            ]));
    }, [
        schema
    ]);
};
export const useRootStyles = (rootNode, mobile)=>{
    return useMemo(()=>{
        let meta = rootNode?.meta;
        if (!meta) return undefined;
        let tColor = meta.themeColor;
        let contrastingColor = getContrastColors(tColor);
        let res = {
            '--presentation_fontFamily': meta.fontFamily,
            ...tColor && {
                '--presentation_themeColor': tColor,
                '--presentation_themeColor_backdrop': contrastingColor?.backdrop,
                '--presentation_themeColor_backdropInvert': contrastingColor?.backdropInvert,
                '--presentation_themeColor_content': contrastingColor?.content,
                '--presentation_themeColor_contentInvert': contrastingColor?.contentInvert,
                '--presentation_navigation_progressBar_bgColor': tColor
            },
            '--presentation_link_color': meta.linkColor,
            '--presentation_bgColor': meta.backgroundColor,
            '--presentation_meta_contentColor': meta.contentColor,
            '--presentation_navigation_bgColor': meta.navigationBgColor,
            '--presentation_navigation_color': meta.navigationColor,
            '--presentation_navigation_progress_bgColor': meta.backgroundColor
        };
        if (meta.fontSize) {
            let fontSize = meta.fontSize.replace('px', '');
            if (mobile) {
                fontSize = Math.round(fontSize * 0.85);
            }
            res = {
                ...res,
                '--presentation_fontSize': `${fontSize}px`
            };
        }
        return res;
    }, [
        rootNode?.meta,
        mobile
    ]);
};
// * shared unsaved/save registry — lets one global save button save all inline editors at once
export const useEditingRegistry = ()=>{
    let entriesRef = useRef({});
    let [unsavedCount, setUnsavedCount] = useState(0);
    let register = useCallback((otid, entry)=>{
        entriesRef.current[otid] = entry;
        setUnsavedCount(keys(entriesRef.current).filter((id)=>entriesRef.current[id].unsaved).length);
    }, []);
    let unregister = useCallback((otid)=>{
        delete entriesRef.current[otid];
        setUnsavedCount(keys(entriesRef.current).filter((id)=>entriesRef.current[id].unsaved).length);
    }, []);
    let saveAll = useCallback(()=>{
        values(entriesRef.current).filter((e)=>e.unsaved).forEach((e)=>callIfFunc(e.save));
    }, []);
    return {
        register,
        unregister,
        saveAll,
        anyUnsaved: unsavedCount > 0
    };
};
